import { useState, useEffect, useRef, useContext, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import AuthContext from "../../store/auth-context";
import { API_NEW_URL, STORAGE_API_URL } from "../../constants/constants";
import { maybeRefreshDomainConfig } from "../../utils/domainConfig";
import { formatLocalDate, formatDuration, formatInviteType } from "./format";
import ImagesPane from "./ImagesPane";
import RecordingsPane from "./RecordingsPane";
import FilesPane from "./FilesPane";
import AISummaryPane from "./AISummaryPane";

const TABS = {
  DETAILS: "details",
  NOTES: "notes",
  IMAGES: "images",
  RECORDINGS: "recordings",
  FILES: "files",
  SUMMARY: "summary",
};

// `optional: true` → CSS @container rules can collapse the tab into the
// More dropdown at narrow widths.
// countField accepts either a string or an array of strings — array values
// are summed (e.g. Files = chatCount + ocrCount, per the Hubspot reference).
const TAB_DEFS = [
  { key: TABS.DETAILS, label: "Details", countField: null, optional: false },
  { key: TABS.IMAGES, label: "Images", countField: "imageCount", optional: false },
  { key: TABS.NOTES, label: "Notes", countField: "notesCount", optional: false },
  { key: TABS.RECORDINGS, label: "Recordings", countField: "recordingCount", optional: true },
  { key: TABS.FILES, label: "Files", countField: ["chatCount", "ocrCount"], optional: true },
  { key: TABS.SUMMARY, label: "AI Summary", countField: "aiSummaryCount", optional: true },
];

// Strip HTML tags from note content (server may store rich text).
const stripHtml = (html) => {
  if (!html) return "";
  return String(html).replace(/<[^>]*>/g, "").trim();
};

// Invite recipient value — email for type 3 (Email), countryCode+mobileNo otherwise.
const getInviteValue = (invite) => {
  if (!invite) return "";
  if (Number(invite.invitationType) === 3) return invite.emailAddress || "";
  const code = invite.countryCode || "";
  const num = invite.mobileNo || "";
  return (code + num).trim();
};

// text() + JSON.parse tolerates empty bodies on 204s.
const safeFetchJson = async (url, token) => {
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
      // Both headers kept additively during the API migration.
      usertoken: token,
      clientappid: process.env.REACT_APP_CLIENTID,
    },
  });
  // SA-change check: no-ops when STORAGE_API_URL responses don't include the header.
  maybeRefreshDomainConfig(res.headers);
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    // Error envelopes seen in the wild:
    //   { error: { code, status, message } } ← new API (e.g. 1018 = no permission)
    //   { message }                           ← legacy
    //   { data: { Error } }                   ← legacy
    const apiErr = data && data.error;
    const message =
      (apiErr && apiErr.message) ||
      (data && data.message) ||
      (data && data.data && data.data.Error) ||
      "Request failed";
    const err = new Error(message);
    err.status = res.status;
    err.code = (apiErr && apiErr.code) || null;
    err.data = data;
    throw err;
  }
  return data;
};

// 1018 = "You don't have appropriate permissions to perform this operation."
// Returned when a lower-role user opens a session created by a higher-role user.
const PERMISSION_CODE = 1018;
const isPermissionError = (info) =>
  !!info && (info.code === PERMISSION_CODE || info.status === 401);

// Azure Blob URLs come with SAS tokens — no auth header needed. The proxy may
// wrap the body in { responseBody: ... } and may inject a UTF-8 BOM that
// breaks JSON.parse if not stripped.
const BOM_RE = /^﻿/;
const fetchBlobJson = async (url) => {
  if (!url) return null;
  try {
    const res = await fetch(url, { method: "GET" });
    let text = await res.text();
    if (!text) return null;
    text = text.replace(BOM_RE, "").trim();
    if (!text) return null;
    const parsed = JSON.parse(text);
    if (
      parsed &&
      typeof parsed === "object" &&
      !Array.isArray(parsed) &&
      parsed.responseBody !== undefined
    ) {
      const inner = parsed.responseBody;
      if (typeof inner === "string") {
        try {
          return JSON.parse(inner.replace(BOM_RE, "").trim());
        } catch {
          return null;
        }
      }
      return inner;
    }
    return parsed;
  } catch {
    return null;
  }
};

const SessionAccordion = ({ session, onBack }) => {
  const authCtx = useContext(AuthContext);

  const sessionId = session && session.sessionId;
  const initialDate = formatLocalDate(
    (session && (session.createdAt || session.createDateTime || session.CreatedAt)) || ""
  );

  const [activeTab, setActiveTab] = useState(TABS.DETAILS);
  const [moreOpen, setMoreOpen] = useState(false);

  // `null` means "not fetched yet" — keeps render and dedupe in sync without
  // an extra hasFetched flag.
  const [detail, setDetail] = useState(null);
  const [detailErr, setDetailErr] = useState(null);
  // Separate from detailErr (string) so renderers can branch on 1018/401 to
  // show a "no permission" view without a Retry button.
  const [detailErrInfo, setDetailErrInfo] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [notes, setNotes] = useState(null);
  const [notesErr, setNotesErr] = useState(null);
  const [notesLoading, setNotesLoading] = useState(false);

  const [invitations, setInvitations] = useState(null);
  const [invitationsLoading, setInvitationsLoading] = useState(false);

  const [recordings, setRecordings] = useState(null);
  const [recordingsErr, setRecordingsErr] = useState(null);
  const [recordingsLoading, setRecordingsLoading] = useState(false);

  const [filesData, setFilesData] = useState(null);
  const [filesErr, setFilesErr] = useState(null);
  const [filesLoading, setFilesLoading] = useState(false);

  const [aiSummary, setAiSummary] = useState(null);
  const [aiSummaryErr, setAiSummaryErr] = useState(null);
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);

  const [, forceRender] = useState(0);

  // Refs (not state) so dedupe survives rapid tab switches without stale
  // closures. paneCacheRef holds placeholder { loading, data } per tab.
  const fetchedRef = useRef({});
  const paneCacheRef = useRef({});
  const moreRef = useRef(null);

  const accordionTitle = session
    ? `${session.title || session.code || sessionId || "Session"} — ${initialDate}`
    : "Session";

  const fetchDetail = useCallback(async () => {
    if (!sessionId) return;
    if (fetchedRef.current.detail) return;
    fetchedRef.current.detail = true;
    setDetailLoading(true);
    setDetailErr(null);
    setDetailErrInfo(null);
    try {
      const data = await safeFetchJson(API_NEW_URL + "sessions/" + sessionId, authCtx.token);
      setDetail(data);
    } catch (err) {
      setDetailErr(err.message || "Failed to load details");
      setDetailErrInfo({ code: err.code, status: err.status });
      fetchedRef.current.detail = false; // allow retry
    } finally {
      setDetailLoading(false);
    }
  }, [sessionId, authCtx.token]);

  const fetchInvitations = useCallback(async () => {
    if (!sessionId) return;
    if (fetchedRef.current.invitations) return;
    fetchedRef.current.invitations = true;
    setInvitationsLoading(true);
    try {
      const data = await safeFetchJson(
        API_NEW_URL + "sessions/" + sessionId + "/invitations",
        authCtx.token
      );
      setInvitations((data && data.items) || []);
    } catch (err) {
      // Soft fail — keep the rest of the Details pane usable.
      setInvitations([]);
      fetchedRef.current.invitations = false; // allow retry on tab re-entry
    } finally {
      setInvitationsLoading(false);
    }
  }, [sessionId, authCtx.token]);

  useEffect(() => {
    // Images tab also needs detail loaded so we can read
    // features.ALLOWZENDESK and choose between the Zendesk-comments hint
    // and the actual image grid.
    if (activeTab === TABS.DETAILS || activeTab === TABS.IMAGES) {
      fetchDetail();
    }
    if (activeTab === TABS.DETAILS) {
      fetchInvitations();
    }
  }, [activeTab, fetchDetail, fetchInvitations]);

  const fetchNotes = useCallback(async () => {
    if (!sessionId) return;
    if (fetchedRef.current.notes) return;
    fetchedRef.current.notes = true;
    setNotesLoading(true);
    setNotesErr(null);
    try {
      const data = await safeFetchJson(
        API_NEW_URL + "sessions/" + sessionId + "/notes?Skip=0&Limit=50",
        authCtx.token
      );
      setNotes((data && data.items) || []);
    } catch (err) {
      setNotesErr(err.message || "Failed to load notes");
      fetchedRef.current.notes = false; // allow retry
    } finally {
      setNotesLoading(false);
    }
  }, [sessionId, authCtx.token]);

  // If the session detail API returned a permission error (1018 / 401), every
  // sub-resource API (notes, recordings, files, summary) will hit the same
  // wall. Skip those calls and zero their tab counts. Computed here so the
  // sub-tab useEffects below can include it in their deps.
  const detailPermissionDenied = isPermissionError(detailErrInfo);

  // Sub-tab fetches wait for detail to *settle* — not just for the permission
  // flag to flip. If a user clicks a sub-tab while detail is still in-flight,
  // detailPermissionDenied is still false (detail hasn't returned yet), so
  // gating only on the flag would race-fire the sub-API. detailLoading covers
  // that window; once detail settles, this re-evaluates and either lets the
  // sub-fetch through or keeps it blocked on permission denied.
  const detailReady = !detailLoading && !detailPermissionDenied;

  useEffect(() => {
    if (activeTab === TABS.NOTES && detailReady) fetchNotes();
  }, [activeTab, fetchNotes, detailReady]);

  // Recordings: same storage endpoint as images, just a different fileType.
  const fetchRecordings = useCallback(async () => {
    if (!sessionId) return;
    if (fetchedRef.current.recordings) return;
    fetchedRef.current.recordings = true;
    setRecordingsLoading(true);
    setRecordingsErr(null);
    try {
      const data = await safeFetchJson(
        STORAGE_API_URL + "v1/Session/GetFiles/" + sessionId + "?fileType=rec",
        authCtx.token
      );
      const body = (data && data.responseBody) || data;
      const list = (body && body.data && body.data.list) || (body && body.list) || [];
      setRecordings(list);
    } catch (err) {
      setRecordingsErr(err.message || "Failed to load recordings");
      fetchedRef.current.recordings = false;
    } finally {
      setRecordingsLoading(false);
    }
  }, [sessionId, authCtx.token]);

  useEffect(() => {
    if (activeTab === TABS.RECORDINGS && detailReady) fetchRecordings();
  }, [activeTab, fetchRecordings, detailReady]);

  // Files: object response with chatList + ocrList (Hubspot reference shape).
  const fetchFiles = useCallback(async () => {
    if (!sessionId) return;
    if (fetchedRef.current.files) return;
    fetchedRef.current.files = true;
    setFilesLoading(true);
    setFilesErr(null);
    try {
      const data = await safeFetchJson(
        STORAGE_API_URL + "v1/Session/" + sessionId + "/files",
        authCtx.token
      );
      const body = (data && data.responseBody) || data;
      const inner = (body && body.data) || body || {};
      setFilesData(inner);
    } catch (err) {
      setFilesErr(err.message || "Failed to load files");
      fetchedRef.current.files = false;
    } finally {
      setFilesLoading(false);
    }
  }, [sessionId, authCtx.token]);

  useEffect(() => {
    if (activeTab === TABS.FILES && detailReady) fetchFiles();
  }, [activeTab, fetchFiles, detailReady]);

  // AI Summary: storage index lists files with Azure blob SAS URLs whose
  // contents are the actual summary JSON. Fetch the index first, then hit
  // each blob URL in parallel and attach the parsed content as
  // `aiSummaryContent` so the pane can render it.
  const fetchAiSummary = useCallback(async () => {
    if (!sessionId) return;
    if (fetchedRef.current.aiSummary) return;
    fetchedRef.current.aiSummary = true;
    setAiSummaryLoading(true);
    setAiSummaryErr(null);
    try {
      const data = await safeFetchJson(
        STORAGE_API_URL + "v1/Session/" + sessionId + "/summary",
        authCtx.token
      );
      const body = (data && data.responseBody) || data;
      const inner = (body && body.data) || body || {};
      const files = Array.isArray(inner.sessionSummaryFiles)
        ? inner.sessionSummaryFiles
        : [];
      if (files.length === 0) {
        setAiSummary(inner);
      } else {
        const enriched = await Promise.all(
          files.map(async (file) => {
            const summaryUrl = (file && file.aiSummary && file.aiSummary.fileUrl) || "";
            const transcriptUrl = (file && file.transcription && file.transcription.fileUrl) || "";
            const [aiSummaryContent, transcriptionContent] = await Promise.all([
              fetchBlobJson(summaryUrl),
              fetchBlobJson(transcriptUrl),
            ]);
            return { ...file, aiSummaryContent, transcriptionContent };
          })
        );
        setAiSummary({ ...inner, sessionSummaryFiles: enriched });
      }
    } catch (err) {
      setAiSummaryErr(err.message || "Failed to load AI summary");
      fetchedRef.current.aiSummary = false;
    } finally {
      setAiSummaryLoading(false);
    }
  }, [sessionId, authCtx.token]);

  useEffect(() => {
    if (activeTab === TABS.SUMMARY && detailReady) fetchAiSummary();
  }, [activeTab, fetchAiSummary, detailReady]);

  useEffect(() => {
    if (!moreOpen) return undefined;
    const onDocClick = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [moreOpen]);

  // Lazy-load + dedupe scaffolding for tabs whose real endpoints aren't
  // wired up yet. Replace the timeout below with the real fetch later.
  useEffect(() => {
    if (
      activeTab === TABS.DETAILS ||
      activeTab === TABS.NOTES ||
      activeTab === TABS.IMAGES ||
      activeTab === TABS.RECORDINGS ||
      activeTab === TABS.FILES ||
      activeTab === TABS.SUMMARY
    )
      return;
    if (fetchedRef.current[activeTab]) return;
    fetchedRef.current[activeTab] = true;
    const tab = activeTab;
    paneCacheRef.current[tab] = { loading: true, data: null, error: null };
    forceRender((x) => x + 1);
    let done = false;
    const t = setTimeout(() => {
      done = true;
      paneCacheRef.current[tab] = { loading: false, data: [], error: null };
      forceRender((x) => x + 1);
    }, 200);
    // If user switches tabs before the timeout fires, clear the dedupe flag
    // so the next activation can start fresh.
    return () => {
      clearTimeout(t);
      if (!done) {
        fetchedRef.current[tab] = false;
        delete paneCacheRef.current[tab];
      }
    };
  }, [activeTab]);

  // Detail shape is flat camelCase but may also nest under `session` — read
  // both so we tolerate backend shape drift.
  const features =
    (detail && (detail.features || (detail.session && detail.session.features))) || {};
  const allowZendesk = Number(features.ALLOWZENDESK) === 1;

  const readCount = (field) => {
    // Detail count (authoritative) falls back to the list-item count.
    const fromDetail =
      detail && (detail[field] ?? (detail.session && detail.session[field]));
    const fromList = session && session[field];
    const c = fromDetail ?? fromList;
    return Number.isFinite(Number(c)) ? Number(c) : 0;
  };

  // Shared error renderer — collapses the 1018/401 permission case into a
  // calm empty-state message (no Retry, since retrying won't help). The
  // message text comes from the BE (`error.message`) so wording stays
  // consistent with the rest of the platform.
  const renderDetailError = () => {
    if (isPermissionError(detailErrInfo)) {
      return (
        <div className="empty-state">
          <div className="es-msg">{detailErr}</div>
        </div>
      );
    }
    return (
      <div className="ch-error">
        {detailErr}
        <div style={{ marginTop: 8 }}>
          <button type="button" className="ch-refresh-btn" onClick={fetchDetail}>
            Retry
          </button>
        </div>
      </div>
    );
  };

  const tabCount = (def) => {
    if (!def.countField) return null;
    if (detailPermissionDenied) return 0;
    const fields = Array.isArray(def.countField) ? def.countField : [def.countField];
    return fields.reduce((sum, f) => sum + readCount(f), 0);
  };

  const renderDetailsPane = () => {
    if (detailLoading) {
      return (
        <div className="loading-state">
          <div className="spinner" />
          <div>Loading…</div>
        </div>
      );
    }
    if (detailErr) return renderDetailError();
    // Detail shape is flat camelCase but may also nest under `session` — read
    // both to absorb backend shape drift.
    const d = detail || {};
    const inner = (d && d.session) || {};
    const pick = (k) => d[k] ?? inner[k] ?? (session && session[k]);

    const agentUrl = pick("agentUrl");
    const guestUrl = pick("guestUrl");
    const duration = pick("duration") ?? pick("callDuration");
    const created = pick("createdAt") ?? pick("createDateTime") ?? pick("CreatedAt");
    const profile = pick("collaborationProfileName");
    const owner = pick("ownerName");
    const inviteType = pick("invitationType");
    const code = pick("code") ?? pick("sessionShortId");

    return (
      <div>
        <div className="detail-meta-row">
          <div className="detail-meta-label">Session ID</div>
          <div className="detail-meta-value">{sessionId || "—"}</div>
        </div>
        {code && (
          <div className="detail-meta-row">
            <div className="detail-meta-label">Code</div>
            <div className="detail-meta-value">{code}</div>
          </div>
        )}
        {profile && (
          <div className="detail-meta-row">
            <div className="detail-meta-label">Profile</div>
            <div className="detail-meta-value">{profile}</div>
          </div>
        )}
        {owner && (
          <div className="detail-meta-row">
            <div className="detail-meta-label">Created by</div>
            <div className="detail-meta-value">{owner}</div>
          </div>
        )}
        {inviteType && (
          <div className="detail-meta-row">
            <div className="detail-meta-label">Invite type</div>
            <div className="detail-meta-value">{formatInviteType(inviteType)}</div>
          </div>
        )}
        <div className="detail-meta-row">
          <div className="detail-meta-label">Duration</div>
          <div className="detail-meta-value">{formatDuration(duration)}</div>
        </div>
        <div className="detail-meta-row">
          <div className="detail-meta-label">Created</div>
          <div className="detail-meta-value">{formatLocalDate(created) || "—"}</div>
        </div>
        {agentUrl && (
          <div className="detail-meta-row">
            <div className="detail-meta-label">Agent URL</div>
            <div className="detail-meta-value">
              <a href={agentUrl} target="_blank" rel="noopener noreferrer">{agentUrl}</a>
            </div>
          </div>
        )}
        {guestUrl && (
          <div className="detail-meta-row">
            <div className="detail-meta-label">Guest URL</div>
            <div className="detail-meta-value">
              <a href={guestUrl} target="_blank" rel="noopener noreferrer">{guestUrl}</a>
            </div>
          </div>
        )}

        <div className="invites-section">
          <div className="invites-title">Invites Sent</div>
          {invitationsLoading ? (
            <div className="invites-loading">
              <div className="spinner spinner-sm" />
              <span>Loading invites…</span>
            </div>
          ) : !invitations || invitations.length === 0 ? (
            <div className="invites-empty">No Invites</div>
          ) : (
            <div className="invites-list">
              {invitations.map((inv, i) => {
                const label = formatInviteType(inv.invitationType);
                const value = getInviteValue(inv) || "—";
                return (
                  <div className="invite-card" key={inv.invitationId || inv.id || i}>
                    <span className="invite-label">{label}:</span>
                    <span className="invite-value">{value}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Images pane mode is driven by the detail response: ALLOWZENDESK=1 →
  // redirect users to the Zendesk ticket comments; ALLOWZENDESK=0 → image
  // upload is disabled in this app so the tab is always empty.
  const renderImagesPane = () => {
    if (detailLoading && !detail) {
      return (
        <div className="loading-state">
          <div className="spinner" />
          <div>Loading…</div>
        </div>
      );
    }
    if (detailErr) return renderDetailError();
    return <ImagesPane useZendeskComments={allowZendesk} />;
  };

  const renderNotesPane = () => {
    // Don't render notes UI when detail denied permission and we never got to
    // fetch notes — fall through to the same permission empty-state.
    if (notes == null && detailPermissionDenied) return renderDetailError();
    // Detail still loading → we haven't fired fetchNotes yet (the gate is
    // waiting). Show a spinner instead of the "no notes" empty state.
    if (notes == null && detailLoading) {
      return (
        <div className="loading-state">
          <div className="spinner" />
          <div>Loading…</div>
        </div>
      );
    }
    if (notesLoading) {
      return (
        <div className="loading-state">
          <div className="spinner" />
          <div>Loading…</div>
        </div>
      );
    }
    if (notesErr) {
      return (
        <div className="ch-error">
          {notesErr}
          <div style={{ marginTop: 8 }}>
            <button type="button" className="ch-refresh-btn" onClick={fetchNotes}>
              Retry
            </button>
          </div>
        </div>
      );
    }
    if (!notes || notes.length === 0) {
      return (
        <div className="empty-state">
          <div className="es-msg">
            No notes available for this session or may have been deleted by your Admin.
          </div>
        </div>
      );
    }
    return (
      <div className="notes-list">
        {notes.map((n, i) => {
          const content = stripHtml(n.noteDetail || n.content || "");
          const created = n.modify_at || n.modifyAt || n.createdAt || n.CreatedAt;
          return (
            <div className="note-card" key={n.noteId || i}>
              {n.title && <div className="note-title">{n.title}</div>}
              {content && <div className="note-content">{content}</div>}
              {(n.creatorName || created) && (
                <div className="note-footer">
                  {n.creatorName && (
                    <div className="note-meta">
                      <span className="lbl">Created by:</span> {n.creatorName}
                    </div>
                  )}
                  {created && (
                    <div className="note-meta">
                      <span className="lbl">Created on:</span> {formatLocalDate(created)}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderPlaceholderPane = (label) => {
    const entry = paneCacheRef.current[activeTab];
    if (!entry || entry.loading) {
      return (
        <div className="loading-state">
          <div className="spinner" />
          <div>Loading…</div>
        </div>
      );
    }
    return (
      <div className="empty-state">
        <div className="es-msg">No {label.toLowerCase()} available for this session.</div>
      </div>
    );
  };

  return (
    <div className="detail-view">
      <div className="detail-header" onClick={onBack} title="Back to list">
        <FontAwesomeIcon icon={faArrowLeft} className="head-chev" />
        <div className="head-title">{accordionTitle}</div>
      </div>
      <div className="detail-tabs" role="tablist">
        {/* Required + visible opt-tabs live in a scroll-area so they can
            scroll horizontally if the container is too narrow (e.g. browser
            zoomed to 110%+), while the More button stays pinned to the right. */}
        <div className="detail-tabs-scroll">
          {TAB_DEFS.map((def) => {
            const c = tabCount(def);
            const label = c == null ? def.label : `${def.label} (${c})`;
            const cls =
              "detail-tab" +
              (def.optional ? " opt-tab" : "") +
              (activeTab === def.key ? " active" : "");
            return (
              <button
                key={def.key}
                type="button"
                role="tab"
                data-tab={def.key}
                className={cls}
                onClick={() => {
                  setActiveTab(def.key);
                  setMoreOpen(false);
                }}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* More dropdown — CSS hides it once every opt-tab fits inline. */}
        <div ref={moreRef} className={"more-tab" + (moreOpen ? " open" : "")}>
          <button
            type="button"
            className="detail-tab"
            onClick={() => setMoreOpen((v) => !v)}
          >
            More <FontAwesomeIcon icon={faChevronDown} className="more-chev" />
          </button>
          {moreOpen && (
            <div className="more-menu" role="menu">
              {TAB_DEFS.filter((d) => d.optional).map((def) => {
                const c = tabCount(def);
                const label = c == null ? def.label : `${def.label} (${c})`;
                return (
                  <button
                    key={def.key}
                    type="button"
                    role="menuitem"
                    data-tab={def.key}
                    className={"dd-item" + (activeTab === def.key ? " active" : "")}
                    onClick={() => {
                      setActiveTab(def.key);
                      setMoreOpen(false);
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div className="detail-body">
        {activeTab === TABS.DETAILS && renderDetailsPane()}
        {activeTab === TABS.NOTES && renderNotesPane()}
        {activeTab === TABS.IMAGES && renderImagesPane()}
        {activeTab === TABS.RECORDINGS &&
          (recordings == null && detailPermissionDenied ? (
            renderDetailError()
          ) : (
            <RecordingsPane
              recordings={recordings}
              isLoading={recordingsLoading || (recordings == null && detailLoading)}
              error={recordingsErr}
              pendingCount={readCount("pendingRecordingCount")}
              onRetry={() => {
                fetchedRef.current.recordings = false;
                fetchRecordings();
              }}
            />
          ))}
        {activeTab === TABS.FILES &&
          (filesData == null && detailPermissionDenied ? (
            renderDetailError()
          ) : (
            <FilesPane
              files={filesData}
              isLoading={filesLoading || (filesData == null && detailLoading)}
              error={filesErr}
              onRetry={() => {
                fetchedRef.current.files = false;
                fetchFiles();
              }}
            />
          ))}
        {activeTab === TABS.SUMMARY &&
          (aiSummary == null && detailPermissionDenied ? (
            renderDetailError()
          ) : (
            <AISummaryPane
              summary={aiSummary}
              isLoading={aiSummaryLoading || (aiSummary == null && detailLoading)}
              error={aiSummaryErr}
              onRetry={() => {
                fetchedRef.current.aiSummary = false;
                fetchAiSummary();
              }}
            />
          ))}
      </div>
    </div>
  );
};

export default SessionAccordion;
